import { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";

interface State {
  error: Error | null;
  errorId: string | null;
}

/**
 * Catches uncaught render errors and shows a branded recovery screen.
 * Wrap the app shell so a single broken page doesn't blank-out the whole UI.
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, errorId: null };

  static getDerivedStateFromError(error: Error): State {
    return {
      error,
      errorId: Math.random().toString(36).slice(2, 10).toUpperCase(),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[error-boundary]", error, info);
  }

  reset = () => this.setState({ error: null, errorId: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="min-h-screen bg-background grid place-items-center px-6 py-16"
        data-testid="error-boundary">
        <div className="max-w-md text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">
            — Something tore a seam
          </p>
          <h1 className="font-display text-3xl">We're on it.</h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            An unexpected error interrupted that view. The rest of your wardrobe is fine.
          </p>
          <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Reference: <span className="font-mono text-foreground/80">{this.state.errorId}</span>
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <button type="button" onClick={this.reset}
              data-testid="error-boundary-retry"
              className="px-5 py-2.5 bg-oxblood hover:bg-oxblood-deep text-ivory text-sm">
              Try again
            </button>
            <Link to="/app"
              onClick={this.reset}
              data-testid="error-boundary-home"
              className="px-5 py-2.5 border border-foreground/20 hover:bg-bone text-sm">
              Back to dashboard
            </Link>
          </div>
        </div>
      </main>
    );
  }
}
